import { Link, useLocation, useNavigate } from "react-router-dom";
import { Home, Package, Gift, Bot, LogIn, LogOut, User } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import KapeiLogo from "@/components/KapeiLogo";
import NotificationsBell from "@/components/NotificationsBell";
import { Button } from "@/components/ui/button";

export default function MobileLayout({ children, hideBottomNav = false }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const navItems = [
    { to: "/", icon: Home, label: "Inicio", testid: "nav-home" },
    { to: "/codes", icon: Package, label: "Mis códigos", testid: "nav-codes" },
    { to: "/club", icon: Gift, label: "Mi KAPEI", testid: "nav-club" },
    { to: "/assistant", icon: Bot, label: "Asistente", testid: "nav-assistant" },
  ];

  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-0 z-40 flex items-center justify-between border-b-2 border-slate-100 bg-white px-4 py-3 lg:px-8">
        <Link to="/" data-testid="header-logo-link">
          <KapeiLogo />
        </Link>
        {user && (
          <nav className="hidden gap-1 lg:flex">
            {navItems.map((it) => {
              const Icon = it.icon;
              const active = location.pathname === it.to;
              return (
                <Link
                  key={it.to}
                  to={it.to}
                  data-testid={`top-${it.testid}`}
                  className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold ${
                    active
                      ? "bg-[#4d7c0f] text-white"
                      : "text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {it.label}
                </Link>
              );
            })}
          </nav>
        )}
        <div className="flex items-center gap-2">
          {user && <NotificationsBell />}
          {user ? (
            <>
              <button
                data-testid="account-btn"
                onClick={() => navigate("/account")}
                className="hidden items-center gap-2 rounded-full px-3 py-2 text-sm font-bold text-slate-700 hover:bg-slate-100 sm:inline-flex"
                title="Mi cuenta"
              >
                <User className="h-4 w-4" /> {user.name}
              </button>
              <Button
                data-testid="logout-btn"
                variant="ghost"
                size="sm"
                onClick={async () => { await logout(); navigate("/"); }}
                className="rounded-full"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <Button
              data-testid="login-btn"
              variant="outline"
              size="sm"
              onClick={() => navigate("/login")}
              className="rounded-full font-bold"
            >
              <LogIn className="mr-1 h-4 w-4" /> Entrar
            </Button>
          )}
        </div>
      </header>

      <main className="mx-auto max-w-md px-4 pb-[calc(7rem+env(safe-area-inset-bottom))] pt-6 lg:max-w-4xl lg:px-8 lg:pb-8">{children}</main>

      {!hideBottomNav && (
        <nav
          data-testid="bottom-nav"
          className="fixed bottom-0 left-0 right-0 z-40 mx-auto flex max-w-md items-center justify-around border-t-2 border-slate-200 bg-white px-2 pb-[calc(0.5rem+env(safe-area-inset-bottom))] pt-2 shadow-[0_-4px_20px_rgba(0,0,0,0.05)] lg:hidden"
        >
          {navItems.map((it) => {
            const active = location.pathname === it.to;
            const Icon = it.icon;
            return (
              <Link
                key={it.to}
                to={it.to}
                data-testid={it.testid}
                className={`flex flex-1 flex-col items-center justify-center gap-1 rounded-xl py-2 transition-colors ${
                  active ? "text-[#4d7c0f]" : "text-slate-500"
                }`}
              >
                <Icon strokeWidth={active ? 3 : 2.25} className="h-6 w-6" />
                <span className="text-xs font-bold">{it.label}</span>
              </Link>
            );
          })}
        </nav>
      )}
    </div>
  );
}
